import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../components/useAuthUser";
import { Helmet } from "react-helmet"; // 👈 import Helmet
import "../styles/Studs.css";

export default function StudsPage() {
  const user = useAuth();
  const [studs, setStuds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        const studsRef = collection(db, "studs");
        const q = query(studsRef, where("available", "==", true));
        const snap = await getDocs(q);
        if (!alive) return;
        setStuds(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
        if (alive) setError("Could not load studs right now.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="studs-page">
      <Helmet>
        <title>Golden Retriever Stud Services | DeClerck’s Goldens</title>
        <meta
          name="description"
          content="Learn about Golden Retriever stud services at DeClerck’s Goldens. Our proven males come from healthy, AKC registered lines with excellent temperaments. Contact us to discuss availability and breeding details."
        />

        {/* Open Graph */}
        <meta property="og:title" content="Golden Retriever Stud Services | DeClerck’s Goldens" />
        <meta
          property="og:description"
          content="DeClerck’s Goldens offers trusted stud services with healthy, AKC registered Golden Retrievers. Contact us directly for details and availability."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.declercksgoldens.com/studs" />
        <meta
          property="og:image"
          content="https://www.declercksgoldens.com/hero-bg-square.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Golden Retriever Stud Services | DeClerck’s Goldens" />
        <meta
          name="twitter:description"
          content="Healthy, AKC registered Golden Retriever studs with strong pedigrees. Contact us to discuss services and availability."
        />
        <meta
          name="twitter:image"
          content="https://www.declercksgoldens.com/hero-bg-square.png"
        />

        {/* Canonical */}
        <link rel="canonical" href="https://www.declercksgoldens.com/studs" />
      </Helmet>

      <h1 className="studs-title">Stud Services at DeClerck’s Goldens</h1>

      {/* Program description */}
      <section className="studs-intro">
        <p>
          For over 30 years, we’ve dedicated ourselves to raising strong, healthy golden
          retrievers with sound genetics and excellent temperaments. Our proven males
          produce happy, healthy litters that carry on the very best of the breed.
        </p>
        <p>
          We now offer stud services to approved breeders who share our commitment to
          quality. Services are available through live, in-person ties, fresh or frozen
          collection for pickup, or convenient shipping.
        </p>
        <p>
          Please note: puppies cannot be held without a deposit. Contact us directly to
          discuss availability, pricing, and to get started.
        </p>
      </section>

      {/* Process overview */}
      <section className="studs-process">
        <h2>Our Stud Process</h2>
        <ol>
          <li><strong>Reach Out</strong> – Contact us to discuss which stud is the best fit.</li>
          <li><strong>Health & Timing</strong> – We’ll review breeding timing with you.</li>
          <li><strong>Service Method</strong> – Choose live tie, fresh, frozen, or shipped collection.</li>
          <li><strong>Deposit & Agreement</strong> – A deposit and signed stud contract are required before services are confirmed.</li>
          <li><strong>Follow-Up</strong> – We provide guidance through the process and are here for support after breeding.</li>
        </ol>
      </section>

      {/* clear scheduling note */}
      <p className="studs-note" aria-live="polite">
        Stud services are scheduled through our contact page so we can confirm timing and availability.
      </p>

      {loading && <div className="studs-loading">Loading…</div>}
      {!!error && <div className="studs-error">{error}</div>}

      {!loading && !error && studs.length === 0 && (
        <div className="no-studs">
          We don’t have any studs listed right now. Contact us to learn about upcoming availability.
        </div>
      )}

      {!loading && !error && studs.length > 0 && (
        <div className="studs-grid">
          {studs.map((stud, i) => {
            const masked = !user;
            return (
              <article
                className="stud-card"
                key={stud.id}
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <header className="stud-img-wrapper" aria-label={`${stud.name} photo`}>
                  <img
                    src={stud.imageUrl}
                    alt={stud.name}
                    className={`stud-img${masked ? " blurred-img" : ""}`}
                    loading="lazy"
                  />
                  {masked && (
                    <div className="stud-card-overlay" aria-hidden="true">
                      <div className="stud-card-title">{stud.name}</div>
                      <div className="stud-card-detail">
                        <b>Login to view details</b>
                      </div>
                    </div>
                  )}
                </header>

                <div className="stud-card-info">
                  {!masked && <h3 className="stud-name">{stud.name}</h3>}

                  {user && (
                    <dl className="stud-meta">
                      {stud.color && (
                        <>
                          <dt>Color</dt>
                          <dd>{stud.color}</dd>
                        </>
                      )}
                      {stud.studFee && (
                        <>
                          <dt>Stud Fee</dt>
                          <dd>${stud.studFee}</dd>
                        </>
                      )}
                      {typeof stud.ofaCertified === "boolean" && (
                        <>
                          <dt>OFA Certified</dt>
                          <dd>{stud.ofaCertified ? "Yes" : "No"}</dd>
                        </>
                      )}
                      {stud.registration && (
                        <>
                          <dt>Registration</dt>
                          <dd>{stud.registration}</dd>
                        </>
                      )}
                      <button
                        className="stud-more-btn"
                        onClick={() => setSelected(stud)}
                        aria-label={`More information about ${stud.name}`}
                      >
                        More Info
                      </button>
                    </dl>
                  )}
                </div>

                {/* CTA */}
                <footer className="stud-card-footer">
                  <Link
                    to="/contact"
                    className="btn contact-cta"
                    aria-label="Contact us about stud services"
                  >
                    <MailIcon />
                    <span>Contact Us</span>
                  </Link>
                </footer>
              </article>
            );
          })}
        </div>
      )}

      {/* Modal mirrors the same CTA */}
      {user && selected && (
        <div
          className="stud-profile-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`${selected.name} details`}
          onClick={() => setSelected(null)}
        >
          <div
            className="stud-profile-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selected.imageUrl}
              alt={selected.name}
              className="stud-profile-img"
              loading="lazy"
            />

            <h2 className="stud-profile-title">{selected.name}</h2>

            <div className="stud-profile-grid">
              {selected.color && <div><b>Color:</b> {selected.color}</div>}
              {selected.studFee && <div><b>Stud Fee:</b> ${selected.studFee}</div>}
              {typeof selected.ofaCertified === "boolean" && (
                <div><b>OFA Certified:</b> {selected.ofaCertified ? "Yes" : "No"}</div>
              )}
              {selected.registration && <div><b>Registration:</b> {selected.registration}</div>}
              {selected.dob && <div><b>Date of Birth:</b> {selected.dob}</div>}
            </div>

            {selected.notes && <p className="stud-notes">{selected.notes}</p>}

            <div className="stud-card-footer" style={{ marginTop: 20 }}>
              <Link
                to="/contact"
                className="btn contact-cta"
                aria-label="Contact us about stud services"
              >
                <MailIcon />
                <span>Contact Us</span>
              </Link>
            </div>

            <button
              className="stud-modal-close"
              onClick={() => setSelected(null)}
              aria-label="Close dialog"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* tiny inline SVG so you do not need an icon lib */
function MailIcon(props) {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      focusable="false"
      style={{ marginRight: 8, flex: "0 0 auto" }}
      {...props}
    >
      <path
        fill="currentColor"
        d="M20 4H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h16a2 2 0 0 0 2-2V6c0-1.1-.9-2-2-2Zm0 4.2-7.2 4.5a1 1 0 0 1-1.06 0L4 8.2V6l8 5 8-5v2.2Z"
      />
    </svg>
  );
}
